import React, { forwardRef } from "react";
import styled from "styled-components";
import { ITheme } from "../Themes/ThemeTypes";

const Wrapper = styled.label<{ theme: ITheme; $error?: boolean }>`
  position: relative;
  display: inline-block;
  width: 100%;
  padding-top: 6px;
  font-family: ${(props) => props.theme.base.typography.font};
  font-size: 16px;
  line-height: 1.5;
`;

const InputEl = styled.input<{ theme: ITheme; $error?: boolean; $hasTrailing?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 56px;
  margin: 0;
  padding: 15px ${(props) => (props.$hasTrailing ? "48px" : "13px")} 15px 13px;
  border: solid 1px
    ${(props) =>
      props.$error ? `rgb(${props.theme.colors.error})` : `rgb(${props.theme.colors.outline})`};
  border-radius: 4px;
  background: transparent;
  color: rgb(${(props) => props.theme.colors.on_surface});
  font: inherit;
  outline: none;
  transition: border-color ${(props) => props.theme.base.motion.duration.short4}ms
    ${(props) => props.theme.base.motion.easing.standard};

  &:focus {
    border-color: ${(props) =>
      props.$error ? `rgb(${props.theme.colors.error})` : `rgb(${props.theme.colors.primary})`};
    border-width: 2px;
    padding: 14px ${(props) => (props.$hasTrailing ? "47px" : "12px")} 14px 12px;
  }
`;

const Trailing = styled.div`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const ErrorText = styled.div<{ theme: ITheme }>`
  margin-top: 4px;
  color: rgb(${(props) => props.theme.colors.error});
  font-size: ${(props) => props.theme.base.typography.body.small.font_size}px;
  line-height: ${(props) => props.theme.base.typography.body.small.line_height}px;
`;

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "ref"> {
  error?: string;
  trailing?: React.ReactNode;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ error, trailing, ...rest }, ref) => (
    <Wrapper $error={!!error}>
      <div style={{ position: "relative" }}>
        <InputEl ref={ref} $error={!!error} $hasTrailing={!!trailing} {...rest} />
        {trailing && <Trailing>{trailing}</Trailing>}
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  ),
);
TextField.displayName = "TextField";

export default TextField;
